import {
	type OrderDetailResponseDto,
	type OrderExaminationResponseDto,
	OrderExaminationStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useEditOrderExaminationMutation } from "Order/Modules/Examination/Api/Mutation/useEditOrderExaminationMutation";
import { EditExaminationModal } from "Order/Modules/Examination/Components/EditExaminationModal";
import {
	type EditOrderExaminationFormModel,
	getEditOrderExaminationFormSchema,
} from "Order/Modules/Examination/Components/Form/Schema/editExaminationFormSchema";
import { usePartnersListQuery } from "Partner/Api/Query/usePartnersListQuery";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import { FormAutocomplete } from "Shared/Components/Form/CustomFormComponent/FormAutocomplete";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid2, Stack } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDeleteOrderExaminationMutation } from "../../Api/Mutation/useDeleteOrderExaminationMutation";

const { form: formResource, shared: sharedResource } =
	Resources.base.examination;

type EditExaminationFormProps = {
	orderExamination: OrderExaminationResponseDto;
	order: OrderDetailResponseDto;
};

export function EditExaminationForm({
	orderExamination,
	order,
}: EditExaminationFormProps) {
	const [isExaminationModalOpen, setIsExaminationModalOpen] = useState(false);
	const { t } = useResource();

	const form = useForm<EditOrderExaminationFormModel>({
		resolver: zodResolver(getEditOrderExaminationFormSchema()),
		defaultValues: orderExamination,
	});

	const {
		control,
		formState: { errors },
	} = form;

	const { data: partners } = usePartnersListQuery({
		verticals: order.verticalId.toString(),
	});

	const { mutate: editExamination, isPending } =
		useEditOrderExaminationMutation(orderExamination.id);
	const { handleServerError } = useHandleServerError(form);

	const partnerOptions = useMemo(
		() =>
			partners?.data?.map(({ partner }) => ({
				value: partner.id,
				label:
					`${partner.name} ${partner.middleName ? `${partner.middleName} ` : ""}${partner.surname}`.trim(),
			})) || [],
		[partners],
	);

	const { mutate: deleteExamination, isPending: isDeletePending } =
		useDeleteOrderExaminationMutation(orderExamination.orderId);
	const handleDeleteExamination = () => {
		deleteExamination(orderExamination.id);
	};

	useEffect(() => {
		form.reset(orderExamination);
	}, [orderExamination]);

	const handleOpenEditExaminationModal = () => {
		setIsExaminationModalOpen(true);
	};

	const handleEditExaminationSubmit = () => {
		handleServerError(editExamination, {});
	};

	const handleExaminationReopen = () => {
		handleServerError(() =>
			editExamination({
				status: OrderExaminationStatus.pending,
			}),
		);
	};

	return (
		<>
			<BaseCard title={t(sharedResource.examination)}>
				<Grid2 container columnSpacing={2}>
					<Grid2 container size={{ xs: 6 }}>
						<Grid2 size={{ xs: 12 }}>
							<DatePicker
								name="examinationDate"
								label={t(formResource.examinationDate)}
								control={control}
								errors={errors}
								sxLabel={{ mt: 0 }}
								disabled={
									orderExamination.status === OrderExaminationStatus.done
								}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12 }}>
							<FormAutocomplete
								id="partner-id"
								name="partnerId"
								label={t(formResource.partner)}
								control={control}
								errors={errors}
								options={partnerOptions}
								sxLabel={{ mt: 0 }}
								fullWidth
								disabled={
									orderExamination.status === OrderExaminationStatus.done
								}
							/>
						</Grid2>
					</Grid2>
					<Grid2 container spacing={2} size={{ xs: 6 }}>
						<Grid2 size={{ xs: 12 }}>
							<Box mb={2}>
								<Input
									id="result"
									name="result"
									label={t(formResource.result)}
									variant="outlined"
									control={control}
									errors={errors}
									multiline
									rows={4}
									sxLabel={{ mt: 0 }}
									fullWidth
									disabled={
										orderExamination.status === OrderExaminationStatus.done
									}
								/>
							</Box>
						</Grid2>
					</Grid2>
				</Grid2>
			</BaseCard>

			<Stack direction="row" gap={1} mt={2} justifyContent="flex-end">
				<Button
					variant="outlined"
					onClick={handleDeleteExamination}
					loading={isDeletePending}
				>
					{t(formResource.delete)}
				</Button>
				{orderExamination.status === OrderExaminationStatus.done && (
					<Button onClick={handleExaminationReopen}>
						{t(formResource.reopen)}
					</Button>
				)}
				{orderExamination.status === OrderExaminationStatus.pending && (
					<Button variant="outlined" onClick={handleOpenEditExaminationModal}>
						{t(formResource.markAsDone)}
					</Button>
				)}
				{orderExamination.status === OrderExaminationStatus.pending && (
					<Button
						variant="contained"
						onClick={handleEditExaminationSubmit}
						loading={isPending}
					>
						{t(formResource.save)}
					</Button>
				)}
			</Stack>
			<EditExaminationModal
				isOpen={isExaminationModalOpen}
				setIsOpen={setIsExaminationModalOpen}
				examinationId={orderExamination.id}
				result={orderExamination.result}
			/>
		</>
	);
}
